// Gradient Tool (G) — paints a gradient between foreground and background colors.
// Click and drag to define gradient direction and length.
//
// Photoshop parity:
// - Linear, Radial, Angle, Reflected, Diamond gradient shapes (all 5)
// - Foreground→Background, Foreground→Transparent, Black & White, and
//   user-defined multi-stop gradients (via GradientEditor)
// - Opacity, Reverse, Dither, Transparency options
// - Shift key constrains to 45° increments

import { GradientCommand } from "../commands/GradientCommand";
import { screenToDoc } from "../render/Viewport";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type GradientShape = "linear" | "radial" | "angle" | "reflected" | "diamond";
export type GradientPreset = "fg-bg" | "fg-transparent" | "bg-transparent" | "black-white" | "custom";

/** One color stop along the gradient bar, position in [0..1]. */
export interface GradientColorStop {
  position: number;
  color: { r: number; g: number; b: number };
}

/** One opacity stop along the gradient bar, position in [0..1], opacity in [0..1]. */
export interface GradientOpacityStop {
  position: number;
  opacity: number;
}

export interface GradientStopSet {
  colorStops: GradientColorStop[];
  opacityStops: GradientOpacityStop[];
}

export class GradientTool implements Tool {
  readonly id = "gradient";
  readonly shortcut = "G";

  shape: GradientShape = "linear";
  preset: GradientPreset = "fg-bg";
  opacity = 1;
  reverse = false;
  dither = true;
  /** When true, opacity stops in customStops are honored; otherwise alpha is forced to 1. */
  transparency = true;
  /** Custom multi-stop set when preset === "custom" (populated by GradientEditor). */
  customStops: GradientStopSet | null = null;

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    bbox: BBox | null;
  } | null = null;

  /** Replace the active multi-stop set (called by GradientEditor). */
  setCustomStops(stops: GradientStopSet | null): void {
    this.customStops = stops;
    if (stops) this.preset = "custom";
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      startX: docX,
      startY: docY,
      endX: docX,
      endY: docY,
      bbox: null,
    };

    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    let docX = p.x - this.active.layer.x;
    let docY = p.y - this.active.layer.y;

    // Shift constrains to 45° increments
    if (s.shiftKey) {
      const dx = docX - this.active.startX;
      const dy = docY - this.active.startY;
      const angle = Math.atan2(dy, dx);
      const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
      const dist = Math.hypot(dx, dy);
      docX = this.active.startX + Math.cos(snapped) * dist;
      docY = this.active.startY + Math.sin(snapped) * dist;
    }

    this.active.endX = docX;
    this.active.endY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, _s: PointerSample): void {
    if (!this.active) return;

    const dx = this.active.endX - this.active.startX;
    const dy = this.active.endY - this.active.startY;
    const dist = Math.hypot(dx, dy);

    // If the drag was tiny, use a default horizontal gradient
    let startX = this.active.startX;
    let startY = this.active.startY;
    let endX = this.active.endX;
    let endY = this.active.endY;

    if (dist < 2) {
      // A click without a meaningful drag falls back to a full-width
      // horizontal gradient. Anchor it at the layer's left edge (x=0)
      // rather than the click position, so it spans the entire layer
      // width regardless of where the user clicked (audit finding 189).
      startX = 0;
      endX = startX + this.active.layer.pixels.width;
    }

    // Compute gradient into a temp buffer — do NOT mutate layer pixels directly.
    // This ensures the render pipeline sees the change only after the command is applied.
    const { layer } = this.active;
    const before = new Uint8ClampedArray(layer.pixels.data);

    const stops = this.resolveStops(ctx);

    const result = computeGradientPixels(
      layer.pixels.data,
      layer.pixels.width,
      layer.pixels.height,
      startX, startY, endX, endY,
      this.shape,
      stops,
      this.opacity,
      this.reverse,
      this.dither,
      this.transparency,
      layer.lockTransparency,
      (ctx.doc as any)?.selection?.mask,
    );

    this.active = null;

    if (!result.bbox) return;

    // Apply gradient pixels to the layer through the command system
    const cmd = new GradientCommand(
      layer.id,
      result.bbox,
      extractRegion(before, layer.pixels.width, result.bbox),
      extractRegion(result.pixels, layer.pixels.width, result.bbox),
    );

    // Write the computed pixels to the layer immediately
    writeRegion(layer.pixels.data, layer.pixels.width, result.bbox, result.pixels);
    layer.pixels.markDirty();

    ctx.emit(cmd);
  }

  /** Map preset + fg/bg colors to a multi-stop set. Used internally + exported for tests. */
  resolveStops(ctx: { fgColor: { r: number; g: number; b: number }; bgColor: { r: number; g: number; b: number } }): GradientStopSet {
    if (this.preset === "custom" && this.customStops) {
      // Defensive copy to avoid mutation from outside.
      return {
        colorStops: this.customStops.colorStops.map(s => ({ position: s.position, color: { ...s.color } })),
        opacityStops: this.customStops.opacityStops.map(s => ({ position: s.position, opacity: s.opacity })),
      };
    }
    const fg = ctx.fgColor;
    const bg = ctx.bgColor;
    switch (this.preset) {
      case "fg-bg":
        return {
          colorStops: [
            { position: 0, color: { ...fg } },
            { position: 1, color: { ...bg } },
          ],
          opacityStops: [
            { position: 0, opacity: 1 },
            { position: 1, opacity: 1 },
          ],
        };
      case "fg-transparent":
        return {
          colorStops: [
            { position: 0, color: { ...fg } },
            { position: 1, color: { ...fg } },
          ],
          opacityStops: [
            { position: 0, opacity: 1 },
            { position: 1, opacity: 0 },
          ],
        };
      case "bg-transparent":
        return {
          colorStops: [
            { position: 0, color: { ...bg } },
            { position: 1, color: { ...bg } },
          ],
          opacityStops: [
            { position: 0, opacity: 1 },
            { position: 1, opacity: 0 },
          ],
        };
      case "black-white":
        return {
          colorStops: [
            { position: 0, color: { r: 0, g: 0, b: 0 } },
            { position: 1, color: { r: 255, g: 255, b: 255 } },
          ],
          opacityStops: [
            { position: 0, opacity: 1 },
            { position: 1, opacity: 1 },
          ],
        };
      default:
        return {
          colorStops: [
            { position: 0, color: { ...fg } },
            { position: 1, color: { ...bg } },
          ],
          opacityStops: [
            { position: 0, opacity: 1 },
            { position: 1, opacity: 1 },
          ],
        };
    }
  }
}

/** Sample a gradient stop set at position `t` (in [0..1]) → {r,g,b,a}. */
export function sampleStops(stops: GradientStopSet, t: number): { r: number; g: number; b: number; a: number } {
  const colors = stops.colorStops.slice().sort((a, b) => a.position - b.position);
  const opacities = stops.opacityStops.slice().sort((a, b) => a.position - b.position);

  // Color interpolation.
  let r = 0, g = 0, b = 0;
  if (colors.length === 0) { r = g = b = 0; }
  else if (colors.length === 1 || t <= colors[0]!.position) {
    r = colors[0]!.color.r; g = colors[0]!.color.g; b = colors[0]!.color.b;
  } else if (t >= colors[colors.length - 1]!.position) {
    const last = colors[colors.length - 1]!.color;
    r = last.r; g = last.g; b = last.b;
  } else {
    for (let i = 0; i < colors.length - 1; i++) {
      const a = colors[i]!, c = colors[i + 1]!;
      if (t >= a.position && t <= c.position) {
        const span = c.position - a.position;
        const lt = span > 0 ? (t - a.position) / span : 0;
        r = a.color.r + (c.color.r - a.color.r) * lt;
        g = a.color.g + (c.color.g - a.color.g) * lt;
        b = a.color.b + (c.color.b - a.color.b) * lt;
        break;
      }
    }
  }

  // Opacity interpolation.
  let alpha = 1;
  if (opacities.length === 0) { alpha = 1; }
  else if (opacities.length === 1 || t <= opacities[0]!.position) {
    alpha = opacities[0]!.opacity;
  } else if (t >= opacities[opacities.length - 1]!.position) {
    alpha = opacities[opacities.length - 1]!.opacity;
  } else {
    for (let i = 0; i < opacities.length - 1; i++) {
      const a = opacities[i]!, c = opacities[i + 1]!;
      if (t >= a.position && t <= c.position) {
        const span = c.position - a.position;
        const lt = span > 0 ? (t - a.position) / span : 0;
        alpha = a.opacity + (c.opacity - a.opacity) * lt;
        break;
      }
    }
  }
  return { r, g, b, a: alpha };
}

/** Pure gradient computation — returns modified pixels and bbox. Does NOT mutate input. */
export function computeGradientPixels(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  shape: GradientShape,
  stops: GradientStopSet,
  opacity: number,
  reverse: boolean,
  dither: boolean,
  transparency: boolean,
  lockTransparency: boolean,
  selMask: Uint8Array | undefined,
): { pixels: Uint8ClampedArray; bbox: BBox | null } {
  const out = new Uint8ClampedArray(data);

  const dx = endX - startX;
  const dy = endY - startY;
  const lenSq = dx * dx + dy * dy;
  const len = Math.sqrt(lenSq) || 1;

  let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      if (selMask && selMask[py * w + px] === 0) continue;

      const idx = (py * w + px) * 4;
      if (lockTransparency && out[idx + 3] === 0) continue;

      let t: number;
      switch (shape) {
        case "linear": {
          t = lenSq > 0 ? ((px - startX) * dx + (py - startY) * dy) / lenSq : 0;
          break;
        }
        case "radial": {
          t = Math.hypot(px - startX, py - startY) / len;
          break;
        }
        case "angle": {
          const angle = Math.atan2(py - startY, px - startX) - Math.atan2(dy, dx);
          // Normalize to [0..1] sweeping clockwise around the start point.
          let norm = angle / (2 * Math.PI);
          norm = norm - Math.floor(norm);
          t = norm;
          break;
        }
        case "reflected": {
          const proj = lenSq > 0 ? ((px - startX) * dx + (py - startY) * dy) / lenSq : 0;
          t = Math.abs(proj);
          break;
        }
        case "diamond": {
          // Project onto gradient axis and its perpendicular, sum |proj| + |perp|.
          if (lenSq > 0) {
            const proj = ((px - startX) * dx + (py - startY) * dy) / lenSq;
            const perp = ((px - startX) * (-dy) + (py - startY) * dx) / lenSq;
            t = Math.abs(proj) + Math.abs(perp);
          } else { t = 0; }
          break;
        }
        default:
          t = 0;
      }

      if (reverse) t = 1 - t;
      t = Math.max(0, Math.min(1, t));

      let ditherOffset = 0;
      if (dither) {
        ditherOffset = ((px % 3) - (py % 3)) / 510;
      }
      t = Math.max(0, Math.min(1, t + ditherOffset));

      const sampled = sampleStops(stops, t);
      const gradR = Math.round(sampled.r);
      const gradG = Math.round(sampled.g);
      const gradB = Math.round(sampled.b);
      const stopA = transparency ? sampled.a : 1;

      const srcR = out[idx]!;
      const srcG = out[idx + 1]!;
      const srcB = out[idx + 2]!;
      const srcA = out[idx + 3]!;

      const a = stopA * opacity;
      const outR = Math.round(srcR * (1 - a) + gradR * a);
      const outG = Math.round(srcG * (1 - a) + gradG * a);
      const outB = Math.round(srcB * (1 - a) + gradB * a);
      const outA = Math.round(srcA * (1 - a) + 255 * a);

      out[idx] = outR;
      out[idx + 1] = outG;
      out[idx + 2] = outB;
      out[idx + 3] = outA;

      if (px < touchX0) touchX0 = px;
      if (py < touchY0) touchY0 = py;
      if (px > touchX1) touchX1 = px;
      if (py > touchY1) touchY1 = py;
    }
  }

  const bbox: BBox | null = touchX1 >= 0
    ? { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 }
    : null;

  return { pixels: out, bbox };
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function writeRegion(
  target: Uint8ClampedArray,
  fullWidth: number,
  bbox: BBox,
  source: Uint8ClampedArray,
): void {
  for (let row = 0; row < bbox.h; row++) {
    const dstOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const srcOff = row * bbox.w * 4;
    target.set(source.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
}
